import * as enabledApi from '@shared/api/enabledPlayApi';
import { isLicensedProfile, isLicenseValidForProfile } from '@shared/constants/profile/ProfileUtils';
import { getLanguageOnly, getSupportedLocale } from '@shared/hooks/helpers/LocalizationHelpers';
import IUserAccess from '@shared/models/components/IUserAccess';
import DeviceProfilesFilterRequest from '@shared/models/deviceProfiles/DeviceProfilesFilterRequest';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import { OSTypes } from '@shared/models/devices/OSTypes';
import IResult from '@shared/models/result/IResult';
import NotFoundResult from '@shared/models/result/NotFoundResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import dayjs from 'dayjs';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import getProfileFeatures from '../getProfileFeatures';
import { publish } from '../helpers/EventCallback';
import { getOnLineStatus } from '../statusBanners/useNetworkStatusBanner';

const PROFILES_LAST_UPDATED_DATE = 'PROFILES_LAST_UPDATED_DATE';
const DEFAULT_PROFILES = 'DEFAULT_PROFILES';

interface PublicProfilesState {
    // State
    defaultProfiles: UserDeviceProfileModel[];
    publicProfiles: UserDeviceProfileModel[];
    isLoading: boolean;
    errors: string[];

    // Actions
    clearProfiles: () => void;
    getPaginatedPublicProfiles: (
        filters: DeviceProfilesFilterRequest,
        os: OSTypes
    ) => Promise<IResult<UserDeviceProfileModel[]>>;
    getProfile: (profileId: string, loadFromStorageOnly?: boolean) => Promise<UserDeviceProfileModel | null>;
    getPublicProfileFeatures: (profileId: string) => any[];
    initializeFromStorage: (locale: string, licenseAccess: IUserAccess) => void;
    isProfileLocaleMatch: (profile: UserDeviceProfileModel, locale: string) => boolean;
    loadDefaultProfilesFromRemote: (
        locale: string,
        os: OSTypes,
        lastUpdatedDate?: string | undefined | null
    ) => Promise<UserDeviceProfileModel[]>;
    loadPublicProfiles: (
        locale: string,
        os: OSTypes,
        licenseAccess: IUserAccess,
        loadFromStorage?: boolean
    ) => Promise<void>;
    saveDefaultProfilesToStorage: (profiles: UserDeviceProfileModel[]) => void;
}

export const usePublicProfilesStore = create<PublicProfilesState>()(
    immer((set, get) => {
        const isProfileLocaleMatch = (profile: UserDeviceProfileModel, locale: string): boolean => {
            if (!profile.defaultLocale) return true;
            const normalizedLocale = locale.toLowerCase();
            const profileLocale = profile.defaultLocale.toLowerCase();
            return (
                getSupportedLocale(profileLocale, 'voice') === normalizedLocale ||
                getLanguageOnly(profileLocale) === getLanguageOnly(normalizedLocale)
            );
        };

        const getFilteredProfiles = (
            profiles: UserDeviceProfileModel[],
            locale: string,
            licenseAccess: IUserAccess
        ) => {
            return profiles.filter(
                (profile) => isProfileLocaleMatch(profile, locale) && isLicenseValidForProfile(profile, licenseAccess)
            );
        };

        const updateLocalDefaultProfiles = (profiles: UserDeviceProfileModel[]) => {
            const sorted = profiles.sort((a, b) => a.name.localeCompare(b.name));
            set((state) => {
                state.defaultProfiles = sorted;
            });
            publish('default-profiles-updated', sorted);
        };

        const loadDefaultProfilesFromStorage = (locale: string, licenseAccess: IUserAccess) => {
            let localDefaultProfiles: UserDeviceProfileModel[] = [];
            try {
                const localProfiles = localStorage.getItem(DEFAULT_PROFILES);
                if (localProfiles) {
                    const parsedProfiles = JSON.parse(localProfiles);
                    localDefaultProfiles = getFilteredProfiles(parsedProfiles, locale, licenseAccess);
                }
            } catch (e) {
                console.log(e);
            }
            return localDefaultProfiles;
        };

        const isProfilesOutdated = (profiles: UserDeviceProfileModel[], locale: string, licenseAccess: IUserAccess) => {
            if (profiles.length === 0) return true;
            const newLocaleProfiles = profiles.filter((p) => isProfileLocaleMatch(p, locale));
            if (newLocaleProfiles.length === 0) return true;
            const freeProfiles = profiles.filter((p) => isLicensedProfile(p));
            if (freeProfiles.length === profiles.length && licenseAccess.type !== 'personal') {
                return true;
            }
            return false;
        };

        const loadDefaultProfilesFromRemote = async (
            locale: string,
            os: OSTypes,
            lastUpdatedDate?: string | undefined | null
        ): Promise<UserDeviceProfileModel[]> => {
            const localDefaultProfiles: UserDeviceProfileModel[] = [];
            try {
                let skip = 0;
                const take = 25;
                let result: IResult<UserDeviceProfileModel[]> = new UnexpectedResult<UserDeviceProfileModel[]>();
                let request: DeviceProfilesFilterRequest = {
                    locale,
                    skip,
                    take,
                    isHidden: false,
                    modifiedDate: lastUpdatedDate ? lastUpdatedDate : undefined,
                    platform: os,
                };
                let hasMore = true;
                while (hasMore) {
                    result = await enabledApi.getPublicProfiles(request);
                    if (result?.resultType === 'Ok' && result.data) {
                        if (result.data.length === 0 || result.data.length < take) {
                            hasMore = false;
                        }
                        localDefaultProfiles.push(...result.data);
                        skip += result.data.length;
                        request = { ...request, skip };
                    } else {
                        set((state) => {
                            state.errors = result.errors ?? ['Error loading default profiles'];
                        });
                        break;
                    }
                }
                return localDefaultProfiles;
            } catch (error) {
                console.error(error);
                set((state) => {
                    state.errors = ['Error loading default profiles'];
                });
                return localDefaultProfiles;
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        };

        const saveDefaultProfilesToStorage = (profiles: UserDeviceProfileModel[]) => {
            try {
                localStorage.setItem(PROFILES_LAST_UPDATED_DATE, new Date().toISOString());
                localStorage.setItem(DEFAULT_PROFILES, JSON.stringify(profiles));
                updateLocalDefaultProfiles(profiles);
            } catch (error) {
                console.error(error);
            }
        };

        const loadDefaultProfiles = async (
            locale: string,
            os: OSTypes,
            licenseAccess: IUserAccess
        ): Promise<IResult<UserDeviceProfileModel[]>> => {
            try {
                set((state) => {
                    state.isLoading = true;
                });

                let existingProfiles = loadDefaultProfilesFromStorage(locale, licenseAccess);

                const lastUpdatedDate =
                    existingProfiles.length > 0 && !isProfilesOutdated(existingProfiles, locale, licenseAccess)
                        ? localStorage.getItem(PROFILES_LAST_UPDATED_DATE)
                        : undefined;

                const newProfiles = await loadDefaultProfilesFromRemote(locale, os, lastUpdatedDate);
                const combinedProfiles = [...newProfiles];

                if (newProfiles.length > 0 && existingProfiles) {
                    for (const profile of existingProfiles) {
                        if (isProfileLocaleMatch(profile, locale)) {
                            const existingProfile = combinedProfiles.find((p) => p.id === profile.id);
                            if (!existingProfile) {
                                combinedProfiles.push(profile);
                            }
                        }
                    }
                }

                if (combinedProfiles.length > 0) {
                    saveDefaultProfilesToStorage(combinedProfiles);
                } else {
                    updateLocalDefaultProfiles(existingProfiles);
                }

                return combinedProfiles.length > 0
                    ? new SuccessResult<UserDeviceProfileModel[]>(combinedProfiles)
                    : new SuccessResult<UserDeviceProfileModel[]>(existingProfiles);
            } catch (error) {
                console.error(error);
                set((state) => {
                    state.errors = ['Error loading default profiles'];
                });
                return new UnexpectedResult<UserDeviceProfileModel[]>();
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        };

        const getProfileById = async (profileId: string, loadFromStorageOnly: boolean = false) => {
            try {
                const existingProfile = get().defaultProfiles.find((p) => p.id === profileId);
                if (existingProfile) {
                    return new SuccessResult<UserDeviceProfileModel>(existingProfile);
                } else if (loadFromStorageOnly) {
                    return new NotFoundResult<UserDeviceProfileModel>();
                } else {
                    const result = await enabledApi.getPublicProfileById(profileId);
                    return result;
                }
            } catch (error) {
                return new UnexpectedResult<UserDeviceProfileModel>();
            }
        };

        const loadPublicProfiles = async (
            locale: string,
            os: OSTypes,
            licenseAccess: IUserAccess,
            loadFromStorage = false
        ) => {
            const isOnline = getOnLineStatus();
            if (!isOnline || loadFromStorage) {
                get().initializeFromStorage(locale, licenseAccess);
                return;
            }
            const defaultProfilesResult = await loadDefaultProfiles(locale, os, licenseAccess);
            if (defaultProfilesResult?.resultType !== 'Ok') {
                get().initializeFromStorage(locale, licenseAccess);
            }
        };

        const initializeFromStorage = (locale: string, licenseAccess: IUserAccess) => {
            try {
                const localDefaultProfiles = loadDefaultProfilesFromStorage(locale, licenseAccess);
                updateLocalDefaultProfiles(localDefaultProfiles);
            } catch (e) {
                console.log(e);
            }
        };

        const getPaginatedPublicProfiles = async (filters: DeviceProfilesFilterRequest, os: OSTypes) => {
            try {
                set((state) => {
                    state.isLoading = true;
                });
                const request = { ...filters, platform: os };
                const result = await enabledApi.getPublicProfiles(request);
                if (result?.resultType === 'Ok') {
                    const sortedProfiles =
                        result.data?.sort((a, b) => dayjs(b.modifiedDate).diff(dayjs(a.modifiedDate))) || [];
                    sortedProfiles.forEach((p, i) => {
                        sortedProfiles[i].features = getProfileFeatures(p.configuration);
                    });
                    set((state) => {
                        state.publicProfiles = sortedProfiles;
                    });
                }
                set((state) => {
                    state.isLoading = false;
                });
                return result;
            } catch (error) {
                console.error(error);
                set((state) => {
                    state.isLoading = false;
                    state.errors = ['Error getting profiles'];
                });
                return new UnexpectedResult<UserDeviceProfileModel[]>();
            }
        };

        const getPublicProfileFeatures = (profileId: string) => {
            const profile = get().publicProfiles.find((p) => p.id === profileId);
            if (!profile) return [];
            return getProfileFeatures(profile.configuration);
        };

        const clearProfiles = () => {
            set((state) => {
                state.defaultProfiles = [];
                state.publicProfiles = [];
            });
            localStorage.removeItem(DEFAULT_PROFILES);
            localStorage.removeItem(PROFILES_LAST_UPDATED_DATE);
        };

        const getProfile = async (profileId: string, loadFromStorageOnly = false) => {
            const profileResult = await getProfileById(profileId, loadFromStorageOnly);
            if (profileResult?.resultType === 'Ok' && profileResult.data) {
                return profileResult.data;
            }
            return null;
        };

        return {
            // State
            defaultProfiles: [],
            publicProfiles: [],
            isLoading: false,
            errors: [],

            // Actions
            clearProfiles,
            getPaginatedPublicProfiles,
            getProfile,
            getPublicProfileFeatures,
            initializeFromStorage,
            isProfileLocaleMatch,
            loadDefaultProfilesFromRemote,
            loadPublicProfiles,
            saveDefaultProfilesToStorage,
        };
    })
);
